import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../../utils/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import OverviewSummaryCards from "./OverviewSummaryCards";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthColors = [
  "#FFDDC1", "#FFABAB", "#FFC3A0", "#D4A5A5", "#85C1E9", "#A3E4D7",
  "#F9E79F", "#AED6F1", "#D7BDE2", "#D5F5E3", "#D6DBDF", "#E8DAEF",
];

const getMostCommonTypeByMonthByYear = (data) => {
  const result = {};

  data.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const type = item.type_name || "Unknown";

    if (!result[year]) {
      result[year] = new Array(12).fill(null).map(() => ({}));
    }

    result[year][month][type] = (result[year][month][type] || 0) + 1;
  });

  const formatted = {};
  Object.keys(result).forEach(year => {
    formatted[year] = result[year].map(monthData => {
      const entries = Object.entries(monthData);
      if (entries.length === 0) return "N/A";
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    });
  });

  return formatted;
};

const forecastNextMonthWorkOrders = (monthlyData) => {
  const currentMonth = new Date().getMonth();
  const prev3 = [monthlyData[(currentMonth - 2 + 12) % 12], monthlyData[(currentMonth - 1 + 12) % 12], monthlyData[currentMonth]];
  const validValues = prev3.filter(v => typeof v === "number");
  if (validValues.length === 0) return 0;
  return Math.round(validValues.reduce((sum, val) => sum + val, 0) / validValues.length);
};

const processYearlyMonthlyData = (data, dateField) => {
  const monthlyData = {};
  data.forEach(item => {
    const date = new Date(item[dateField]);
    const year = date.getFullYear();
    const month = date.getMonth();
    if (!monthlyData[year]) {
      monthlyData[year] = new Array(12).fill(0);
    }
    monthlyData[year][month]++;
  });
  return monthlyData;
};

const OverviewWorkOrders = () => {
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [loading, setLoading] = useState(true);
  const [workOrdersData, setWorkOrdersData] = useState([]);
  const [viewMode, setViewMode] = useState("all");

  const handleViewChange = (event, newView) => {
    if (newView !== null) setViewMode(newView);
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/getWorkOrders`);
      setWorkOrdersData(res.data);
    } catch (error) {
      console.error("Error fetching work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyDataByYear = processYearlyMonthlyData(workOrdersData, "created_at");
  const selectedYearData = monthlyDataByYear[viewMode] || new Array(12).fill(0);

  const yearColors = ["#A3E4D7", "#85C1E9", "#FFDDC1", "#FFC3A0", "#D7BDE2", "#AED6F1"];
  const summaryChartData = {
    labels: monthLabels,
    datasets: Object.entries(monthlyDataByYear).map(([year, data], idx) => ({
      label: year,
      data,
      backgroundColor: yearColors[idx % yearColors.length],
    })),
  };

  const mostCommonTypesByMonthByYear = getMostCommonTypeByMonthByYear(workOrdersData);
  const forecastCount = forecastNextMonthWorkOrders(selectedYearData);

  const exportReport = (format) => {
    const dateField = "created_at";
    const filteredData = workOrdersData.filter((item) => {
      const itemDate = new Date(item[dateField]);
      const start = filter.startDate ? new Date(filter.startDate) : null;
      const end = filter.endDate ? new Date(filter.endDate) : null;
      return (!start || itemDate >= start) && (!end || itemDate <= end);
    });

    if (filteredData.length === 0) {
      alert("No records found in the selected date range.");
      return;
    }

    const fileName = `report_work_orders_${new Date().toISOString()}`;

    if (format === "PDF") {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Work Orders Report', 14, 20);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);

      const rawHeaders = Object.keys(filteredData[0] || {});
      const headers = rawHeaders.map(h => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      const body = filteredData.map(item => rawHeaders.map(key => item[key] || ""));

      doc.autoTable({
        head: [headers],
        body: body,
        styles: { font: 'courier', fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        startY: 35,
        margin: { left: 14, right: 14 },
        theme: 'grid',
      });

      doc.save(`${fileName}.pdf`);
    } else if (format === "CSV") {
      const headers = Object.keys(filteredData[0]);
      const csvRows = [headers.join(",")];
      filteredData.forEach(item => {
        const values = headers.map(header => `"${String(item[header] || "").replace(/"/g, '""')}"`);
        csvRows.push(values.join(","));
      });

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  return (
    <Box>
      <Card sx={{ marginBottom: 4, padding: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              name="startDate"
              value={filter.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              name="endDate"
              value={filter.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => exportReport("CSV")} startIcon={<FileDownloadIcon />}>
              Export CSV
            </Button>
            <Button variant="contained" onClick={() => exportReport("PDF")} startIcon={<FileDownloadIcon />}>
              Export PDF
            </Button>
          </Grid>
        </Grid>
      </Card>
      <Grid item xs={12}>
  <OverviewSummaryCards stats={{
    total: workOrdersData.length,
    thisMonth: workOrdersData.filter(w => new Date(w.created_at).getMonth() === new Date().getMonth()).length,
    resolved: workOrdersData.filter(w => w.status?.toLowerCase() === 'resolved').length,
    pending: workOrdersData.filter(w => w.status?.toLowerCase() !== 'resolved').length,
    avgResolution: (() => {
      const durations = workOrdersData
        .filter(w => w.resolved_at)
        .map(w => (new Date(w.resolved_at) - new Date(w.created_at)) / (1000 * 60 * 60 * 24));
      return durations.length ? `${(durations.reduce((a, b) => a + b) / durations.length).toFixed(1)} days` : "N/A";
    })()
  }} />
</Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6">📊 Work Orders Trends (Monthly)</Typography>
            <Box sx={{ height: 300 }}>
              {loading ? <Typography>Loading...</Typography> : <Bar data={summaryChartData} />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6">🔮 Forecast for Next Month ({viewMode})</Typography>
            <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h2" fontWeight="bold" color="primary">
                  {forecastCount}
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ padding: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">🏆 Most Common Work Order Type by Month</Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewChange}
                size="small"
                color="primary"
              >
                <ToggleButton value="all">All</ToggleButton>
                {Object.keys(mostCommonTypesByMonthByYear).map(year => (
                  <ToggleButton key={year} value={year}>{year}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {monthLabels.map((month, idx) => (
                  <Grid item xs={6} sm={4} md={2} key={idx}>
                    <Card
  sx={{
    p: 2,
    height: viewMode === "all" ? 120 : 90,
    overflowY: viewMode === "all" ? "auto" : "visible",
    display: "flex",
    flexDirection: "column",
    backgroundColor: monthColors[idx % monthColors.length],
    color: "#000",
  }}
>
  <Typography variant="subtitle2">{month}</Typography>
  <Box sx={{ flex: 1 }}>
    {viewMode === "all" ? (
      Object.entries(mostCommonTypesByMonthByYear).map(([year, values]) => (
        <Typography key={year} variant="body2" noWrap>
          {year}: <strong>{values[idx]}</strong>
        </Typography>
      ))
    ) : (
      <Typography variant="body2">
        <strong>{mostCommonTypesByMonthByYear[viewMode]?.[idx] || "N/A"}</strong>
      </Typography>
    )}
  </Box>
</Card> </Grid>
                ))}
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewWorkOrders;
